/**
 * Schreibwege der Übernahme (0064). Sie bekommen den Datenanschluss übergeben, damit dieselben Funktionen in der App und in
 * den Tests gegen die Demo-Datenbank laufen. Rechte, Nur-Lesen-Phase und alle Prüfungen liegen in der Datenbank.
 */
import type { Api } from "../../../data/api";
import { ApiError } from "../../../data/api";
import { sha256Hex, storageSafeName } from "../../../model/invoice-document";
import { attachmentEInvoiceFields, prepareUpload, serverPruefen } from "../../../model/e-rechnung";
import { fold } from "./import-model";
import type { KoopImport, RechnungImport } from "./altbestand-model";
import { t } from "../../../lib/sprache";

/**
 * Eine Kooperation übernehmen: deal_save (wie das Formular, mit p_months) und der Stand vergangener Monate in einem Schritt
 * (public.deal_import). Neue Marken entstehen dabei; spätere Zeilen derselben neuen Marke nutzen sie (brandIds).
 */
export async function importKooperation(api: Api, orgId: string, v: KoopImport, brandIds: Map<string, string>): Promise<{ dealId: string; months: number }> {
  const known = v.newBrand ? brandIds.get(fold(v.newBrand)) : undefined;
  const values = { ...v.deal, org_id: orgId, ...(known ? { brand_id: known } : {}) };
  const r = await api.rpc<{ deal_id: string; months: number; brand_id: string }>("deal_import", {
    p_values: values, p_terms: v.terms, p_months: v.months, p_states: v.states, p_new_brand: v.newBrand && !known ? v.newBrand : null,
  });
  if (v.newBrand && !known && r.brand_id) brandIds.set(fold(v.newBrand), r.brand_id);
  return { dealId: r.deal_id, months: Number(r.months ?? 0) };
}

/** Eine offene Rechnung übernehmen (invoice_import_legacy): versendet bzw. freigegeben, ohne Mails und Glocken. */
export async function importRechnung(api: Api, orgId: string, v: RechnungImport): Promise<{ invoiceId: string; status: string; openCents: number }> {
  const r = await api.rpc<{ invoice_id: string; status: string; open_cents: number }>("invoice_import_legacy", { p_org: orgId, p_invoice: v });
  return { invoiceId: r.invoice_id, status: r.status, openCents: Number(r.open_cents ?? 0) };
}

export const MAX_PDF_BYTES = 15 * 1024 * 1024;

/**
 * Original-PDF einer übernommenen Rechnung nachreichen: in den Ordner der Rechnung laden, als Anlage eintragen und als
 * Rechnungs-PDF setzen (invoice_import_attach_pdf). Danach gilt es für Anzeige, Versand und Mahnung.
 */
export async function attachImportedPdf(api: Api, orgId: string, invoiceId: string, file: Blob, fileName: string): Promise<string> {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(fileName);
  if (!isPdf) throw new ApiError(t("Bitte das Original als PDF-Datei wählen.", "Please choose the original as a PDF file."), { code: "P0001" });
  if (file.size <= 0) throw new ApiError(t("Die Datei ist leer.", "The file is empty."), { code: "P0001" });
  if (file.size > MAX_PDF_BYTES) throw new ApiError(t("Die Datei ist größer als 15 MB.", "The file is larger than 15 MB."), { code: "P0001" });
  const bytes = new Uint8Array(await file.arrayBuffer());
  const name = storageSafeName(fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`);
  const path = `${orgId}/invoice/${invoiceId}/${Date.now()}_${name}`;
  const pdf = file.type === "application/pdf" ? file : new Blob([bytes as BlobPart], { type: "application/pdf" });
  // L12: ZUGFeRD/Factur-X erkennen (Kurzfassung und Prüfsumme an der Anlage); ein reines XML nimmt dieser Weg nicht an
  const prepared = await prepareUpload(pdf, name);
  await api.upload("documents", path, pdf);
  const rows = await api.insert<{ id: string }>("attachments", {
    org_id: orgId, owner_kind: "invoice", owner_id: invoiceId, storage_path: path, file_name: fileName.slice(-200) || "Rechnung.pdf",
    mime_type: "application/pdf", size_bytes: file.size, category: "invoice", ...attachmentEInvoiceFields(prepared),
  });
  await serverPruefen(api, { attachment: rows?.[0]?.id, path }, prepared);
  await api.rpc("invoice_import_attach_pdf", { p_invoice: invoiceId, p_path: path, p_sha256: await sha256Hex(bytes) });
  return path;
}
